
import { Component, Vue } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { PERMISSION } from '@/permissions';
import { decodeOrder } from '@/utils/decoding';
import { DateTime } from 'luxon';
import { Debounce, cancelDebounce } from '@/utils/decorators';
import { OrdersTableFilters } from '@/store/types';
import Toast from '@/utils/toast';
import InfoBanner from '@/components/shared/suites/InfoBanner.vue';
import SuitesHeader from '@/components/shared/suites/SuitesHeader.vue';
import OrderManagementList from '@/components/order-management/OrderManagementList.vue';
import OrderManagementTableList from '@/components/order-management/OrderManagementTableList.vue';
import OrderModal from '@/components/order-management/OrderModal.vue';
import BulkActionsModal from '@/components/order-management/BulkActionsModal.vue';
import CancelledOrdersModal from '@/components/order-management/CancelledOrdersModal.vue';
import FullPageSpinner from '@/components/shared/suites/FullPageSpinner.vue';
import TextButton from '@/components/shared/suites/TextButton.vue';
import PauseOrdersModal from '@/components/order-management/PauseOrdersModal.vue';
import ImportOrdersModal from '@/components/order-management/ImportOrdersModal.vue';
import DatePicker from '@/components/shared/DatePicker.vue';
import CateringFilters from '@/components/order-management/CateringFilters.vue';
import K12Filters from '@/components/order-management/K12Filters.vue';
import TextField from '@/components/shared/TextField.vue';

const namespace: string = 'orders';

@Component<OrderManagement>({
	components: {
		TextField,
		InfoBanner,
		SuitesHeader,
		OrderManagementList,
		OrderManagementTableList,
		OrderModal,
		BulkActionsModal,
		CancelledOrdersModal,
		FullPageSpinner,
		TextButton,
		PauseOrdersModal,
		ImportOrdersModal,
		DatePicker,
		CateringFilters,
		K12Filters
	}
})
export default class OrderManagement extends Vue {
	@Action('fetchOrders', { namespace }) private fetchOrders!: (filters: OrderFilterParams) => Promise<FetchOrdersResponse | undefined>;
	@Action('fetchMarketplaceOrders', { namespace }) private fetchMarketplaceOrders!: (lastCreatedAt? : string) => Promise<{ areThereNewOrders: boolean, fetchDate: string } | undefined>;
	@Action('fetchPizzabotOrders', { namespace }) private fetchPizzabotOrders!: (lastCreatedAt? : string) => Promise<{ areThereNewOrders: boolean, fetchDate: string } | undefined>;
	@Getter('getCurrentOrders', { namespace }) private orders!: Order[] | CurrentOrders;
	@Getter('hasPermission', { namespace: 'auth' }) private hasPermission!: (permission: PERMISSION) => boolean;
	@Getter('getRestaurant', { namespace: 'auth' }) private restaurant!: Restaurant;
	@Getter('isK12Location', { namespace: 'auth' }) private isK12Location!: boolean;
	@Getter('isCateringRestaurant', { namespace: 'auth' }) private isCateringRestaurant!: boolean;
	@Getter('isMarketplaceEnabled', { namespace: 'auth' }) private isMarketplaceEnabled!: boolean;
	@Getter('isPizzabotV2Location', { namespace: 'auth' }) private isPizzabotV2Location!: boolean;
	@Getter('areNotificationSoundsAllowed', { namespace: 'auth' }) private areNotificationSoundsAllowed!: boolean;
	@Getter('allowBulkComplete', { namespace: 'auth' }) private allowBulkComplete!: boolean;
	@Getter('allowBulkCancel', { namespace: 'auth' }) private allowBulkCancel!: boolean;
	@Getter('areOrdersPaused', { namespace: 'auth' }) private ordersPaused!: boolean;
	@Getter('hasExtraPaymentMethodsConfigured', { namespace: 'auth' }) private hasExtraPaymentMethodsConfigured!: boolean;

	//TODO: should clean up the loading states.
	private isDataFetching: boolean = false;
	private isFetchingNewDateOrders: boolean = true;
	private pausingOrders: boolean = false;
	private loadingStatus: boolean = false;
	private isTableLoading: boolean = false;
	private intervalId: number = 0;
	private currentPage: number = 1;
	private itemsPerPage: number = 25;
	private paginateBy: number = 25;
	private statusTotals: { pending: number; inProgress: number; completed: number } = { pending: 0, inProgress: 0, completed: 0 };
	private totalCount: number = 0;
	private lastFetchDate: string|undefined = undefined;
	private ordersFetchingDate: string = DateTime.now().setZone(this.restaurant?.menu_configuration?.zone || 'America/Toronto').toISODate();
	private searchInput: string = '';
	private ordersViewTypeToggle: string = 'kanban';
	private sortBy: string = 'pickup_time';
	private sortDirection: 'asc' | 'desc' = 'desc';
	private selectedOrder: Order | undefined = {} as Order;
	private PERMISSION = PERMISSION;

	private modal: { [key: string]: boolean } = {
		order: false,
		cancelledOrders: false,
		bulkActions: false,
		pauseOrdersMessage: false,
		importOrders: false
	};

	private tableFilters: OrdersTableFilters = {
		selectedGrades: [],
		selectedClasses: [],
		selectedTeachers: [],
		selectedStatuses: [],
		selectedTypes: []
	};

	private get showCreateOrderButton(): boolean {
		// Check that all contact fields are required and not hidden
		const contactFieldsConfig = this.restaurant.menu_manager_config?.contact_fields_config as Record<string, ContactFieldSettings> | undefined;
		const contactFieldsRequired = ['name', 'email', 'phone_number'].every(field => !!(contactFieldsConfig?.[field]?.required && !contactFieldsConfig?.[field]?.hide));

		return this.hasPermission(PERMISSION.ADMIN_ONLY) || (this.restaurant.catering && contactFieldsRequired && this.hasExtraPaymentMethodsConfigured);
	}

	/**
	 * Fetch the orders on creation, then every 10 seconds.
	 * The spinner only shows up for the first load, after that
	 * we don't need to, orders update in realtime and there is
	 * a small animation per section.
	 *
	 * @return {Promise<void>}
	 */
	private async created(): Promise<void> {
		this.ordersViewTypeToggle = this.isK12Location ? 'list_table' : 'kanban';
		this.isDataFetching = true;
		this.fetchCurrentOrders();
		this.setFetchInterval();

		// Check for search query in URL and perform search if present
		const searchQuery = this.$route.query.search;
		if (searchQuery && typeof searchQuery === 'string') {
			this.ordersViewTypeToggle = 'list_table';
			this.searchInput = searchQuery;
			this.debouncedSearch();
		}
	}

	/**
	 * Remove the fetch orders interval
	 *
	 * @return {void}
	 */
	private beforeDestroy(): void {
		cancelDebounce(this.debouncedSearch);
		clearInterval(this.intervalId);
	}

	/**
	 * Debounced search method that updates the actual search string after user stops typing
	 *
	 * @return {Promise<void>}
	 */
	@Debounce(500)
	private async debouncedSearch(): Promise<void> {
		this.currentPage = 1;
		this.isFetchingNewDateOrders = true;
		await this.fetchCurrentOrders();
	}

	/**
	 * Handle view toggle changes to reload orders with default filters
	 *
	 * @return {Promise<void>}
	 */
	private async handleViewToggleChange(): Promise<void> {
		this.currentPage = 1;
		this.isFetchingNewDateOrders = true;

		// Clear status filters when switching to kanban view
		if (this.ordersViewTypeToggle === 'kanban') {
			this.tableFilters.selectedStatuses = [];
		}

		await this.fetchCurrentOrders();
	}

	/**
	 * Strip the restaurant's internal identifier prefix from an order search query,
	 * so searching "A73" (or "#A73") finds order 73.
	 *
	 * @param {string} search
	 * @return {string}
	 */
	private stripOrderSearchPrefix(search: string): string {
		const identifier: string | null | undefined = this.restaurant.internal_identifier;
		if (!identifier) {
			return search;
		}

		const candidate: string = search.startsWith('#') ? search.slice(1) : search;

		// While the query is still a partial, an empty string is returned so no filtering happens yet
		if (candidate && identifier.toLowerCase().startsWith(candidate.toLowerCase())) {
			return '';
		}

		if (candidate.toLowerCase().startsWith(identifier.toLowerCase())) {
			// The prefix is only stripped when the remainder is purely numeric, leaving other search terms untouched
			const remainder: string = candidate.slice(identifier.length);
			if (/^\d+$/.test(remainder)) {
				return remainder;
			}
		}

		return search;
	}

	/**
	 * Fetch orders and set the state.
	 *
	 * @param {{ id?: number; status?: string; }} orderInfo
	 * @return {Promise<void>}
	 */
	private async fetchCurrentOrders(orderInfo: { id?: number; status?: string; } = {}): Promise<void> {
		try {
			let response: { areThereNewOrders: boolean; fetchDate: string; statusTotals?: { pending: number; inProgress: number; completed: number; }; totalCount?: number; } | undefined;

			if (this.isMarketplaceEnabled) {
				response = await this.fetchMarketplaceOrders(this.lastFetchDate);
			}
			else if (this.isPizzabotV2Location) {
				response = await this.fetchPizzabotOrders(this.lastFetchDate);
			}
			else {
				const filterParams: OrderFilterParams = {
					categorized: this.ordersViewTypeToggle === 'kanban',
					lastOrder: this.lastFetchDate,
					search: this.searchInput ? this.stripOrderSearchPrefix(this.searchInput) || undefined : undefined,
					status: this.tableFilters.selectedStatuses.length ? JSON.stringify(this.tableFilters.selectedStatuses) : undefined,
					grades: this.tableFilters.selectedGrades.length ? JSON.stringify(this.tableFilters.selectedGrades) : undefined,
					classes: this.tableFilters.selectedClasses.length ? JSON.stringify(this.tableFilters.selectedClasses) : undefined,
					teachers: this.tableFilters.selectedTeachers.length ? JSON.stringify(this.tableFilters.selectedTeachers) : undefined,
					page: this.currentPage,
					limit: this.ordersViewTypeToggle === 'kanban' ? this.itemsPerPage : this.paginateBy,
					sortBy: this.ordersViewTypeToggle === 'list_table' ? this.sortBy : 'id',
					sortDirection: this.ordersViewTypeToggle === 'list_table' ? this.sortDirection : 'desc'
				};

				if (this.tableFilters.selectedTypes.length) {
					filterParams.type = JSON.stringify(this.tableFilters.selectedTypes);
				}

				// Add date filters for K12 locations
				if (this.isK12Location) {
					filterParams.startDate = this.ordersFetchingDate;
					filterParams.endDate = this.ordersFetchingDate;
				}

				response = await this.fetchOrders(filterParams);
			}

			if (response?.areThereNewOrders && this.areNotificationSoundsAllowed) {
				const audio: HTMLAudioElement = new Audio(require('@/assets/audio/notification.ogg'));
				audio.play().catch(() => {});
			}
			this.lastFetchDate = response?.fetchDate;
			this.statusTotals = response?.statusTotals || { pending: 0, inProgress: 0, completed: 0 };
			this.totalCount = response?.totalCount || 0;
		}
		catch (errorMessage) {
			Toast.error(this.$t('orders.management.error_fetching_orders', { errorMessage }));
		}
		finally {
			this.isDataFetching = false;
			this.isFetchingNewDateOrders = false;
			if (orderInfo.id) {
				this.selectedOrder = decodeOrder((this.orders as Order[]).find(order => order.id === orderInfo.id) as Order);
			}
			if (orderInfo.status && this.selectedOrder) {
				this.selectedOrder.status = orderInfo.status;
			}
		}
	}

	/**
	 * Set interval to fetch orders every 10 seconds
	 *
	 * @return {void}
	 */
	private setFetchInterval(): void {
		this.intervalId = window.setInterval(() => {
			if (!this.ordersPaused) {
				this.fetchCurrentOrders();
			}
		}, 10000);
	}

	/**
	 * Show modal for the specific order
	 *
	 * @param {Order} order
	 * @return {void}
	 */
	private showModal(order: Order): void {
		this.selectedOrder = decodeOrder(order);
		this.modal.order = true;
	}

	/**
	 * Handle the date change event
	 *
	 * @param {string} date
	 * @return {Promise<void>}
	 */
	private async handleDateChange(date: string): Promise<void> {
		this.currentPage = 1;
		this.isFetchingNewDateOrders = true;
		this.ordersFetchingDate = date;
		await this.fetchCurrentOrders();
	}

	/**
	 * Handle filter changes from the table list component
	 *
	 * @param {OrdersTableFilters} filters
	 * @return {Promise<void>}
	 */
	private async handleFiltersChanged(filters: OrdersTableFilters): Promise<void> {
		this.isTableLoading = true;

		this.tableFilters = {
			selectedGrades: filters.selectedGrades,
			selectedClasses: filters.selectedClasses,
			selectedTeachers: filters.selectedTeachers,
			selectedStatuses: filters.selectedStatuses,
			selectedTypes: filters.selectedTypes
		};

		if (filters.currentPage !== undefined) {
			this.currentPage = filters.currentPage;
		}
		if (filters.itemsPerPage !== undefined) {
			this.itemsPerPage = filters.itemsPerPage;
		}

		await this.fetchCurrentOrders();
		this.isTableLoading = false;
	}

	/**
	 * Handle sorting changes from the table component
	 *
	 * @param {{ sortBy: string; sortDirection: 'asc' | 'desc' }} sortInfo
	 * @return {Promise<void>}
	 */
	private async handleSortChanged(sortInfo: { sortBy: string; sortDirection: 'asc' | 'desc' }): Promise<void> {
		this.isTableLoading = true;
		this.currentPage = 1;
		this.sortBy = sortInfo.sortBy;
		this.sortDirection = sortInfo.sortDirection;
		await this.fetchCurrentOrders();
		this.isTableLoading = false;
	}

	/**
	 * Handle load more from Kanban component
	 * Increase the limit by itemsPerPage amount to load more orders
	 *
	 * @param {string} status - The status to load more orders for
	 * @return {Promise<void>}
	 */
	private async handleLoadMore(): Promise<void> {
		this.loadingStatus = true;
		this.itemsPerPage += this.paginateBy;
		await this.fetchCurrentOrders();
		this.loadingStatus = false;
	}

	/**
	 * Handle individual filter changes from K12 filters component
	 *
	 * @param {{ filterName: string; value: string[] }} filterChange
	 * @return {Promise<void>}
	 */
	private async handleFilterChanged(filterChange: { filterName: string; value: string[] }): Promise<void> {
		(this.tableFilters as any)[filterChange.filterName] = filterChange.value;
		await this.applyFilters();
	}

	/**
	 * Apply filters by making a new API call
	 *
	 * @return {Promise<void>}
	 */
	private async applyFilters(): Promise<void> {
		this.currentPage = 1;
		this.isFetchingNewDateOrders = true;
		await this.fetchCurrentOrders();
	}

	/**
	 * Clear all filters and refresh data
	 *
	 * @return {Promise<void>}
	 */
	private async clearFilters(): Promise<void> {
		this.isFetchingNewDateOrders = true;
		this.tableFilters = {
			selectedGrades: [],
			selectedClasses: [],
			selectedTeachers: [],
			selectedStatuses: [],
			selectedTypes: []
		};
		this.currentPage = 1;
		await this.fetchCurrentOrders();
	}

	/**
	 * Clear the search input and search string
	 *
	 * @return {Promise<void>}
	 */
	private async clearSearch(): Promise<void> {
		this.searchInput = '';
		this.currentPage = 1;
		this.isFetchingNewDateOrders = true;
		await this.fetchCurrentOrders();
	}

	/**
	 * Open the orders status screen page in a new tab.
	 *
	 * @return {void}
	 */
	private openOrdersStatusScreen(): void {
		window.open('/order-management/customer-display', '_blank');
	}
}
